import React, { useState } from 'react';
import { generateImage, downloadImage } from '../services/imageService';
import { nanoid } from 'nanoid';

const models = [
  { id: 'flux', name: '通用模型 (Flux)' },
  { id: 'flux-realism', name: '写实风格 (Flux Realism)' },
  { id: 'any-dark', name: '暗黑风格 (Dark)' },
  { id: 'flux-anime', name: '动漫风格 (Anime)' },
  { id: 'flux-3d', name: '3D风格' },
  { id: 'turbo', name: '快速模式 (Turbo)' },
];

const aspectRatios = [
  { width: 1024, height: 1024, name: '1:1 方形' },
  { width: 1024, height: 768, name: '4:3 横向' },
  { width: 768, height: 1024, name: '3:4 纵向' },
  { width: 1024, height: 576, name: '16:9 宽屏' },
];

function isWeixinBrowser() {
  return navigator.userAgent.toLowerCase().includes('micromessenger');
}

function Generate() {
  const [formData, setFormData] = useState({
    prompt: '',
    model: 'flux',
    width: 1024,
    height: 1024,
  });
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const handleAspectRatioChange = (width, height) => {
    setFormData(prev => ({ ...prev, width, height }));
  };

  const generateRandomSeed = () => {
    return Math.floor(Math.random() * 1000000000);
  };

  const handleSubmit = async (e, customSeed = null) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setGeneratedImage(null);
    setImageLoading(true);

    try {
      const params = {
        width: formData.width,
        height: formData.height,
        model: formData.model,
        seed: customSeed || generateRandomSeed(),
        nologo: 'true'
      };

      const imageUrl = await generateImage(formData.prompt, params);
      
      // 创建一个新的图片对象来预加载图片
      const img = new Image();
      img.src = imageUrl;
      
      img.onload = () => {
        // 生成一个唯一的图片ID
        const imageId = nanoid();
        
        // 存储图片信息
        setGeneratedImage({
          id: imageId,
          url: imageUrl,
          prompt: formData.prompt,
          params: {
            ...formData,
            seed: params.seed
          }
        });
        setImageLoading(false);
      };

      img.onerror = () => {
        setError('图片生成失败，请重试');
        setImageLoading(false);
      };
    } catch (err) {
      setError(err.message);
      setImageLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      if (!generatedImage?.url) return;
      await downloadImage(generatedImage.url, generatedImage.prompt);
    } catch (err) {
      setError('下载图片失败，请重试');
    }
  };

  const promptSuggestions = [
    "font text: happy new year 2025, China Luna new year, golden cute 3d snake ,octane render ,8K",
    "Golden snake,among flowers and peonies,golden paper fragments,red background,ultra detail,",
    "一只可爱的猫咪在花园里玩耍，动漫风格",
    "未来科技城市的夜景，赛博朋克风格",
    "山间的传统日式寺庙，晨雾缭绕",
    "宇宙空间站的控制室，科幻写实风格",
  ];

  const handlePromptSuggestion = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      prompt: suggestion,
    }));
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gray-800 rounded-2xl shadow-xl p-6 mb-8">
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-blue-400 mb-4">灵感提示</h2>
            <div className="flex flex-wrap gap-2">
              {promptSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handlePromptSuggestion(suggestion)}
                  className={`px-4 py-2 text-sm ${
                    index === 0 || index === 1 
                      ? 'bg-red-700 text-yellow-300 hover:bg-red-600' 
                      : 'bg-gray-700 text-blue-300 hover:bg-gray-600'
                  } rounded-full transition-colors duration-200`}
                >
                  {index === 0 ? '新春贺图' : index === 1 ? '新春贺图2' : suggestion}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="prompt" className="block text-sm font-medium text-gray-300 mb-2">
                创作提示词
              </label>
              <textarea
                id="prompt"
                value={formData.prompt}
                onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-100 placeholder-gray-400"
                rows="4"
                placeholder="描述你想要创作的图像，越详细越好..."
                required
              />
            </div>

            <div>
              <label htmlFor="model" className="block text-sm font-medium text-gray-300 mb-2">
                生成模型
              </label>
              <select
                id="model"
                value={formData.model}
                onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-100"
              >
                {models.map(model => (
                  <option key={model.id} value={model.id} className="bg-gray-700">
                    {model.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                图像尺寸
              </label>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {aspectRatios.map((ratio) => (
                  <button
                    key={`${ratio.width}x${ratio.height}`}
                    type="button"
                    onClick={() => handleAspectRatioChange(ratio.width, ratio.height)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      formData.width === ratio.width && formData.height === ratio.height
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {ratio.name}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || imageLoading}
              className="w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading || imageLoading ? '正在生成图像...' : '生成图像'}
            </button>
          </form>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-900/50 border border-red-500 text-red-300 rounded-lg">
            {error}
          </div>
        )}

        {imageLoading && (
          <div className="bg-gray-800 rounded-2xl shadow-xl p-6 text-center">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-32 h-32 relative">
                <div className="w-full h-full rounded-full absolute border-4 border-gray-700 opacity-25"></div>
                <div className="w-full h-full rounded-full absolute border-4 border-blue-500 border-t-transparent animate-spin"></div>
              </div>
              <p className="mt-4 text-gray-400">正在生成您的艺术作品...</p>
              <p className="mt-2 text-sm text-gray-500">这可能需要一些时间</p>
            </div>
          </div>
        )}

        {generatedImage && !imageLoading && (
          <div className="bg-gray-800 rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-semibold text-blue-400 mb-4">生成结果</h2>
            <div className="overflow-hidden rounded-lg bg-gray-700">
              {/* 使用 CSS 禁用右键菜单和拖拽，但在微信中允许长按 */}
              <div 
                className="relative w-full"
                onContextMenu={(e) => !isWeixinBrowser() && e.preventDefault()}
                style={{ userSelect: 'none' }}
              >
                <img
                  src={generatedImage.url}
                  alt={generatedImage.prompt}
                  className={`w-full h-auto ${!isWeixinBrowser() ? 'pointer-events-none' : ''}`}
                  draggable={isWeixinBrowser()}
                />
                {/* 添加一个透明的覆盖层来防止直接与图片交互，在微信中不添加 */}
                {!isWeixinBrowser() && <div className="absolute inset-0 bg-transparent"></div>}
              </div>
            </div>
            <div className="mt-4 text-sm text-gray-400">
              <p>使用的种子值：{generatedImage.params.seed}</p>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row justify-end gap-4">
              <button
                onClick={(e) => handleSubmit(e)}
                className="px-6 py-3 text-sm font-medium text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                随机重新生成
              </button>
              <button
                onClick={(e) => handleSubmit(e, generatedImage.params.seed)}
                className="px-6 py-3 text-sm font-medium text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                使用相同种子重新生成
              </button>
              <button
                onClick={handleDownload}
                className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                {isWeixinBrowser() ? '保存图像' : '下载图像'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Generate;
